import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { 
  Plus, Edit2, Trash2, Eye, FileText, Search,
  Calendar, Clock, Filter, Check, X, Sparkles, 
  RefreshCw, Bot, Zap, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBlogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // AI Generation state
  const [aiStats, setAiStats] = useState(null);
  const [aiTopics, setAiTopics] = useState([]);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');

  // Initial fetch for static data
  useEffect(() => {
    fetchCategories();
    fetchAiStats();
    fetchAiTopics();
  }, []);

  // Fetch blogs when filters change
  useEffect(() => {
    fetchBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, categoryFilter]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blogs/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAiStats = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/blogs/ai/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiStats(response.data);
    } catch (error) {
      console.error('Error fetching AI stats:', error);
    }
  };

  const fetchAiTopics = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/blogs/ai/topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiTopics(response.data.topics || []);
    } catch (error) {
      console.error('Error fetching AI topics:', error);
    }
  };

  const fetchBlogs = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);

      const response = await axios.get(`${API}/admin/blogs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(response.data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to load blogs');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blogId) => {
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog deleted successfully');
      setDeleteConfirm(null);
      fetchBlogs();
      fetchAiStats();
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Failed to delete blog');
    }
  };

  const handleStatusChange = async (blogId, newStatus) => {
    const token = localStorage.getItem('token');
    
    try {
      await axios.put(`${API}/admin/blogs/${blogId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Blog ${newStatus === 'published' ? 'published' : 'unpublished'} successfully`);
      fetchBlogs();
      fetchAiStats();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleGenerateAi = async (useRandom = true) => {
    const token = localStorage.getItem('token');
    setGenerating(true);
    
    try {
      const params = new URLSearchParams();
      if (!useRandom && selectedTopic && selectedCategory) {
        params.append('topic', selectedTopic);
        params.append('category', selectedCategory);
      }
      
      const response = await axios.post(
        `${API}/admin/blogs/ai/generate?${params}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('AI article generated! Check drafts to review.');
      fetchBlogs();
      fetchAiStats();
      setShowAiPanel(false);
      setSelectedTopic('');
      setSelectedCategory('');
    } catch (error) {
      console.error('Error generating article:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate article');
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTopicsForCategory = (cat) => {
    const categoryData = aiTopics.find(t => t.category === cat);
    return categoryData?.topics || [];
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-playfair text-2xl font-bold text-[#1a1a1a]">Blog Management</h1>
            <p className="font-manrope text-sm text-[#737373]">Create, manage, and auto-generate insights articles</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAiPanel(!showAiPanel)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-manrope text-sm hover:from-purple-700 hover:to-indigo-700 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              AI Generate
            </button>
            <Link
              to="/admin/blogs/create"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg font-manrope text-sm hover:bg-[#B8960F] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Write Manually
            </Link>
          </div>
        </div>

        {/* AI Generation Panel */}
        {showAiPanel && (
          <div className="mb-8 bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Bot className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-playfair text-lg font-bold text-[#1a1a1a]">AI Article Generator</h3>
                <p className="font-manrope text-sm text-[#737373]">Powered by Claude Sonnet 4.5</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Category Selection */}
              <div>
                <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
                  Category (optional)
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setSelectedTopic('');
                  }}
                  className="w-full border border-purple-200 rounded-lg px-3 py-2 font-manrope text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none bg-white"
                >
                  <option value="">Random Category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              {/* Topic Selection */}
              <div>
                <label className="block font-manrope text-sm font-medium text-[#1a1a1a] mb-2">
                  Topic (optional)
                </label>
                <select
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  disabled={!selectedCategory}
                  className="w-full border border-purple-200 rounded-lg px-3 py-2 font-manrope text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Random Topic</option>
                  {getTopicsForCategory(selectedCategory).map((topic, idx) => (
                    <option key={idx} value={topic}>{topic}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <p className="font-manrope text-xs text-purple-600">
                <Zap className="w-3 h-3 inline mr-1" />
                Articles are generated as drafts for your review before publishing
              </p>
              <button
                onClick={() => handleGenerateAi(!(selectedTopic && selectedCategory))}
                disabled={generating}
                className="inline-flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg font-manrope text-sm hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate Article
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* AI Stats */}
        {aiStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-[#e5e5e5] rounded-lg p-4">
              <p className="font-manrope text-sm text-[#737373]">Total Articles</p>
              <p className="font-playfair text-2xl font-bold text-[#1a1a1a]">{blogs.length}</p>
            </div>
            <div className="bg-white border border-[#e5e5e5] rounded-lg p-4">
              <p className="font-manrope text-sm text-[#737373]">Published</p>
              <p className="font-playfair text-2xl font-bold text-[#16a34a]">
                {blogs.filter(b => b.status === 'published').length}
              </p>
            </div>
            <div className="bg-white border border-[#e5e5e5] rounded-lg p-4">
              <p className="font-manrope text-sm text-[#737373]">Drafts (Review)</p>
              <p className="font-playfair text-2xl font-bold text-[#d97706]">
                {blogs.filter(b => b.status === 'draft').length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
              <p className="font-manrope text-sm text-purple-600 flex items-center gap-1">
                <Bot className="w-4 h-4" />
                AI Generated
              </p>
              <p className="font-playfair text-2xl font-bold text-purple-700">
                {aiStats.total_ai_articles}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#737373]" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-[#e5e5e5] rounded-lg px-3 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
            >
              <option value="">All Status</option>
              <option value="draft">Draft (Pending Review)</option>
              <option value="published">Published</option>
            </select>
          </div>
          
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-[#e5e5e5] rounded-lg px-3 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37] focus:outline-none"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Blog List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="font-manrope text-[#737373]">Loading blogs...</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-16 bg-[#fafafa] rounded-lg">
            <FileText className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
            <h3 className="font-playfair text-xl font-bold mb-2 text-[#1a1a1a]">No Articles Yet</h3>
            <p className="font-manrope text-[#737373] mb-6">
              Start by generating an AI article or writing manually
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowAiPanel(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-manrope text-sm hover:from-purple-700 hover:to-indigo-700 transition-all"
              >
                <Sparkles className="w-4 h-4" />
                Generate with AI
              </button>
              <Link
                to="/admin/blogs/create"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#e5e5e5] rounded-lg font-manrope text-sm hover:bg-[#f5f5f5] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Write Manually
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#fafafa] border-b border-[#e5e5e5]">
                <tr>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-wider text-[#737373]">
                    Article
                  </th>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-wider text-[#737373]">
                    Category
                  </th>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-wider text-[#737373]">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-wider text-[#737373]">
                    Source
                  </th>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-wider text-[#737373]">
                    Views
                  </th>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-wider text-[#737373]">
                    Date
                  </th>
                  <th className="text-right px-4 py-3 font-manrope text-xs uppercase tracking-wider text-[#737373]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e5e5e5]">
                {blogs.map(blog => (
                  <tr key={blog.id} className="hover:bg-[#fafafa]">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#f5f5f5] rounded-lg overflow-hidden flex-shrink-0">
                          {blog.featured_image ? (
                            <img 
                              src={blog.featured_image.startsWith('/') ? `${BACKEND_URL}${blog.featured_image}` : blog.featured_image}
                              alt={blog.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className={`w-full h-full flex items-center justify-center ${
                              blog.ai_generated 
                                ? 'bg-gradient-to-br from-purple-500 to-indigo-500' 
                                : 'bg-gradient-to-br from-[#D4AF37] to-[#B8960F]'
                            }`}>
                              {blog.ai_generated ? (
                                <Bot className="w-5 h-5 text-white/70" />
                              ) : (
                                <FileText className="w-5 h-5 text-white/50" />
                              )}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-manrope font-semibold text-[#1a1a1a] line-clamp-1">
                            {blog.title}
                          </p>
                          <p className="font-manrope text-xs text-[#737373]">
                            {blog.read_time} min read
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="px-2 py-1 bg-[#f5f5f5] text-[#737373] rounded text-xs font-manrope">
                        {blog.category}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleStatusChange(blog.id, blog.status === 'published' ? 'draft' : 'published')}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-manrope ${
                          blog.status === 'published'
                            ? 'bg-[#dcfce7] text-[#16a34a]'
                            : 'bg-[#fef3c7] text-[#d97706]'
                        }`}
                      >
                        {blog.status === 'published' ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <Clock className="w-3 h-3" />
                        )}
                        {blog.status}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      {blog.ai_generated ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-manrope">
                          <Bot className="w-3 h-3" />
                          AI
                        </span>
                      ) : (
                        <span className="text-xs text-[#737373]">Manual</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className="font-mono text-sm text-[#737373]">{blog.views}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="font-manrope text-sm text-[#737373]">
                        {formatDate(blog.published_at || blog.created_at)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {blog.status === 'published' && (
                          <a
                            href={`/insights/${blog.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-[#737373] hover:text-[#1a1a1a] hover:bg-[#f5f5f5] rounded transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                        )}
                        <Link
                          to={`/admin/blogs/edit/${blog.id}`}
                          className="p-2 text-[#737373] hover:text-[#D4AF37] hover:bg-[#fee2e2] rounded transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setDeleteConfirm(blog.id)}
                          className="p-2 text-[#737373] hover:text-[#dc2626] hover:bg-[#fee2e2] rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
              <h3 className="font-playfair text-xl font-bold text-[#1a1a1a] mb-2">
                Delete Article?
              </h3>
              <p className="font-manrope text-[#737373] mb-6">
                This action cannot be undone. The article will be permanently deleted.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 border border-[#e5e5e5] rounded-lg font-manrope text-sm hover:bg-[#f5f5f5] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="flex-1 px-4 py-2 bg-[#dc2626] text-white rounded-lg font-manrope text-sm hover:bg-[#b91c1c] transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBlogs;
